import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'

import { playEvent } from '@/helpers/sound'
import { createBridge } from '@/api/acepokerBridge'
import type {
  JoinAction,
  Player,
  PlayerId,
  RoomState,
  SitDownAction,
  WebsocketMessage
} from '@acepoker/contract/acepoker'
import { removeGameToken } from '@/helpers/storage'
import { SuggestedActionQuality } from '@acepoker/api-core'
import type { SuggestedAction } from '@acepoker/contract'

export const GameState = {
  INIT: 'init',
  ERROR: 'error',
  DISCONNECTED: 'disconnected',
  GAME: 'game',
  PREGAME: 'pregame',
  CLOSED: 'closed'
} as const
export type GameState = (typeof GameState)[keyof typeof GameState]

const EMPTY_ROOM_STATE: RoomState = {
  name: '',
  hand: null,
  seats: [],
  scores: {},
  players: [],
  ledger: [],
  handNumber: -1,

  gameConfig: {
    learningMode: false,
    blinds: [10, 20]
  },
  max_players: 9,
  suggestedActions: [],
  sessionId: '',
  playerTimeout: 0,

  hostId: null,

  handHistory: [],
  roomHistory: [],
  isNewHandStarting: false
}

export const useGameState = defineStore('gameState', () => {
  let pingInterval: ReturnType<typeof setInterval> | null = null
  let bridge: ReturnType<typeof createBridge> | null = null
  const loadState = ref<'init' | 'loading' | 'loaded' | 'error' | 'disconnected' | 'closed'>(
    'loading'
  )
  const roomState = ref<RoomState>(EMPTY_ROOM_STATE)
  const errorMessage = ref('')
  const playerId = ref<PlayerId | null>(null)
  const autoAction = ref<'fold' | 'check' | null>(null)
  const winners = ref<string[]>([])
  const actionPending = ref(false)
  const dealerIndex = ref(0)
  const suggestedActions = ref<SuggestedAction[]>([])
  const lastAction = ref<SuggestedAction | null>(null)
  const isReviewMode = ref(false)
  const isHintMode = ref(false)
  const isHintMove = ref(false)
  const showButtons = ref(false)
  const playerCardsVisible = ref(false)
  const correctMovesCount = ref(0)
  const roomId = ref<string | null>(null)
  const gameMode = ref<'drill' | 'compete' | null>(null)
  const seatedTableId = ref<string>('')
  const getSeatedTableId = computed((): string => seatedTableId.value)
  const gameStateUpdateListeners = ref<
    Record<string, (update: { state: RoomState; playerId: string }) => void>
  >({})
  const roomHistoryList = ref<[] | null>([])
  const getRoomHistoryList = computed(() => roomHistoryList.value)

  const isSpectator = computed(
    () => !roomState.value.players.find((el) => el.playerId === playerId.value)
  )

  const score = computed(() => (playerId.value ? roomState.value.scores[playerId.value] : 0))
  const delta = ref(0)

  const removingBotIds = ref<string[]>([])

  const markBotAsRemoving = (id: string): void => {
    if (!removingBotIds.value.includes(id)) {
      removingBotIds.value.push(id)
    }
  }

  const clearBotAsRemoving = (id: string): void => {
    const index = removingBotIds.value.indexOf(id)
    if (index > -1) {
      removingBotIds.value.splice(index, 1)
    }
  }

  const getMarkBotAsRemoving = computed(() => removingBotIds.value)

  watch(isSpectator, (isNowSpectator, wasSpectator) => {
    if (wasSpectator && !isNowSpectator) {
      actionPending.value = false
    }
  })

  /* QA specific, please keep */
  watch(
    () => roomState.value.handNumber,
    (newValue, oldValue) => {
      if (!roomId.value || newValue === 0) return

      console.log('Starting new hand')
      console.log(
        [
          `http://`,
          window.location.hostname,
          `:16686`,
          `/search?`,
          `lookback=24h`,
          `&operation=hand`,
          `&service=poker-server`,
          `&tags=%7B%22hand%22%3A%22`,
          newValue,
          `%22%2C%22`,
          `tableId%22%3A%22`,
          roomId.value,
          `%22%7D`
        ].join('')
      )
    }
  )

  watch(
    () => score.value,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        delta.value = newValue - oldValue
      }
    }
  )

  const isMyTurn = computed(() => {
    if (!roomState.value.hand) {
      return false
    }
    return roomState.value.hand.activePlayerId === playerId.value && !actionPending.value
  })

  const currentState = computed<GameState>(() => {
    if (loadState.value === 'loading') {
      return GameState.INIT
    }

    if (loadState.value === 'error') {
      return GameState.ERROR
    }

    if (loadState.value === 'closed') {
      return GameState.CLOSED
    }

    if (loadState.value === 'disconnected') {
      return GameState.DISCONNECTED
    }

    if (roomState.value.hand) {
      return GameState.GAME
    }

    return GameState.PREGAME
  })

  const getPlayerById = (id: PlayerId): Player => {
    const player = roomState.value.players.find(
      (player) => player.playerId.toString() === id.toString()
    )!
    // if (!player) {
    //   throw new Error('Player not found')
    // }
    return player
  }
  const player = computed(() => (playerId.value != null ? getPlayerById(playerId.value) : null))

  const getSeatForPlayer = (id: PlayerId) =>
    roomState.value.seats.find((seat) => seat?.playerId === id)

  const highestBet = computed(() => {
    if (!roomState.value.hand) return 0
    return Math.max(0, ...Object.values(roomState.value.hand.bets))
  })
  const myBet = computed(() => roomState.value.hand?.bets[playerId.value!] || 0)
  const callAmount = computed(() => {
    return highestBet.value - myBet.value
  })

  const setSeatedTableId = (table_id: string) => {
    if (table_id) {
      seatedTableId.value = table_id
    }
  }

  const clearSeatedTableId = () => {
    seatedTableId.value = ''
  }

  function applyAutoAction() {
    if (autoAction.value === 'fold') {
      if (callAmount.value === 0) {
        bridge!.check()
      } else {
        bridge!.fold()
      }
    } else if (autoAction.value === 'check') {
      if (callAmount.value === 0) {
        bridge!.check()
      }
    }

    autoAction.value = null
  }

  // compete
  watch(
    () => isMyTurn.value,
    (newValue, oldValue) => {
      if (!oldValue && newValue && autoAction.value) {
        applyAutoAction()
      }
    }
  )
  watch(
    [() => roomState.value.hand?.communityCards.length, () => roomState.value.handNumber],
    () => {
      autoAction.value = null
    }
  )

  // practice
  watch(
    () => isMyTurn.value,
    (newValue, oldValue) => {
      if (!oldValue && newValue) {
        showButtons.value = true
        isReviewMode.value = false
        isHintMove.value = false
        actionPending.value = false
      }
      if (oldValue && !newValue) {
        isReviewMode.value = true

        if (
          lastAction.value?.quality === SuggestedActionQuality.Best ||
          lastAction.value?.quality === SuggestedActionQuality.Correct
        ) {
          correctMovesCount.value += 1
        }
        if (
          lastAction.value?.quality === SuggestedActionQuality.Wrong ||
          lastAction.value?.quality === SuggestedActionQuality.Blunder
        ) {
          correctMovesCount.value = 0
        }
      }
    }
  )

  watch(
    () => roomState.value.suggestedActions,
    () => {
      if (!isMyTurn.value) return
      suggestedActions.value = roomState.value.suggestedActions
      isReviewMode.value = false
      lastAction.value = null
    }
  )

  watch(
    () => roomState.value.hand,
    (newValue) => {
      winners.value = newValue?.winners || []

      if (roomState.value.hand && roomState.value.hand.dealerId) {
        const newDealerIndex = roomState.value.seats.findIndex(
          (seat) => seat?.playerId === roomState.value.hand!.dealerId
        )
        if (newDealerIndex !== -1) {
          dealerIndex.value = newDealerIndex
        }
      }
    }
  )

  watch(
    () => getSeatForPlayer(playerId.value!),
    (_, oldValue) => {
      if (oldValue == null) {
        playEvent('sit')
      }
    }
  )
  watch(
    () => roomState.value.hand?.activePlayerId,
    (newValue) => {
      if (newValue && roomState.value.hand!.activePlayerId === playerId.value!) {
        playEvent('your_turn')
      }
    }
  )

  watch(
    () => roomState.value.hand?.communityCards.length,
    (newValue) => {
      if (newValue === 3) {
        playEvent('flop')
      } else if (newValue === 4) {
        playEvent('turn')
      } else if (newValue === 5) {
        playEvent('river')
      }
    }
  )

  watch(
    () => roomState.value.handNumber,
    (newNo, oldNo) => {
      if (oldNo === -1) return
      if (newNo !== oldNo) {
        showButtons.value = false
        isReviewMode.value = false
        isHintMove.value = false
        lastAction.value = null
        suggestedActions.value = []
      }
    }
  )

  watch(
    [() => roomState.value.hand, () => roomState.value.hand?.winners?.length ?? 0],
    ([hand, winnersLen]) => {
      if (!hand || winnersLen > 0) {
        showButtons.value = false
        isReviewMode.value = false
        isHintMove.value = false
        lastAction.value = null
        suggestedActions.value = []
      }
    }
  )

  return {
    requestAutoAction(newAction: 'fold' | 'check') {
      autoAction.value = newAction === autoAction.value ? null : newAction
    },

    gameState: currentState,
    winners,
    dealerIndex,
    autoAction,
    actionPending,

    lastAction,
    score,
    delta,

    showButtons,
    isReviewMode,
    isHintMode,
    isHintMove,
    suggestedActions,
    roomId,
    gameMode,

    isSpectator,

    loadState,
    correctMovesCount,
    roomState,
    errorMessage,
    getPlayerById,
    getSeatForPlayer,
    getRoomHistoryList,
    getMarkBotAsRemoving,
    markBotAsRemoving,
    clearBotAsRemoving,
    getActivePlayerState(id: PlayerId) {
      if (!roomState.value.hand) {
        return null
      }

      if (roomState.value.hand.activePlayerId !== id) {
        return null
      }

      return {
        // activeTimeout: roomState.value.hand?.activeTimeout ?? 0
        activeTimeout: roomState.value.playerTimeout ?? 0
      }
    },
    getHoleCardsForPlayer(id: PlayerId) {
      if (!roomState.value.hand) {
        return null
      }

      return roomState.value.hand.holeCards[id]
    },

    playerCardsVisible,
    player,
    isOwner: computed(() => playerId.value === roomState.value.hostId),
    isGameStarted: computed(
      () => Boolean(roomState.value.hand?.dealerId) || roomState.value.handHistory?.length
    ),
    isMyTurn,
    highestBet,
    isCardLost: (card: string | null) => {
      return Boolean(
        card &&
          roomState.value.hand?.winners.length &&
          !roomState.value.hand?.winnerCards.includes(card)
      )
    },
    myBet,
    callAmount,
    totalPot: computed(() => {
      if (!roomState.value.hand) {
        return 0
      }

      return roomState.value.hand.totalPot
    }),

    async bet(amount: number) {
      bridge!.bet(amount)
    },

    async raise(amount: number) {
      bridge!.raise(amount)
    },

    async fold() {
      bridge!.fold()
    },

    async call() {
      bridge!.call()
    },

    async check() {
      bridge!.check()
    },

    async leaveTable() {
      bridge?.leaveTable()
    },

    async sitOut() {
      bridge!.sitOut()
    },

    async sitIn() {
      bridge!.sitIn()
    },

    async standUp() {
      bridge!.standUp()
    },

    async closeTable() {
      bridge!.closeTable()
    },

    async joinRoom(payload: JoinAction & { roomId: string; name: string }) {
      loadState.value = 'loading'
      roomId.value = payload.roomId

      bridge = createBridge({
        ...payload,
        onReady() {
          bridge!.join(payload)
        },
        onJoin(message) {
          gameMode.value = message.gameMode
          actionPending.value = false
          const PING_INTERVAL = 1000
          pingInterval = setInterval(() => {
            bridge!.ping()
          }, PING_INTERVAL)
        },
        onError(message: string) {
          loadState.value = 'error'
          errorMessage.value = message
        },
        onRoomStateUpdate: (updates: WebsocketMessage[]) => {
          for (const message of updates) {
            if (message.type === 'full') {
              loadState.value = 'loaded'
              this.updateGameState(message)
              playerId.value = message.playerId
            } else if (message.type === 'delta') {
              this.applyDelta(message)
              playerId.value = message.playerId
            }
          }
        },
        onClose: () => {
          if (loadState.value !== 'closed') {
            loadState.value = 'disconnected'
          }
          if (pingInterval) {
            clearInterval(pingInterval)
          }
        },
        onTableClosed: () => {
          loadState.value = 'closed'
        }
      })
    },

    applyDelta(message: never) {
      mergeDeltaToRoomState(roomState.value, message.diff)
      actionPending.value = false
      Object.keys(gameStateUpdateListeners.value).forEach((key) => {
        gameStateUpdateListeners.value[key]({
          state: roomState.value,
          playerId: message.playerId
        })
      })

      if (message.diff.roomHistory) {
        const log = message.diff.roomHistory[0]
        if (
          log &&
          !roomHistoryList.value.some((existing) => existing.createdAt === log.createdAt)
        ) {
          roomHistoryList.value.push(log)
        }
      }
    },

    async sitDown(payload: Omit<SitDownAction, 'action'>) {
      actionPending.value = true
      const name = payload.name ?? 'Player'
      bridge!.sitDown({ ...payload, name })
    },

    updateGameState(message: WebsocketMessage) {
      if (message.type === 'full') {
        // Check for top-ups
        if (message.state.handNumber !== roomState.value.handNumber) {
          showButtons.value = false
          isReviewMode.value = false
          isHintMove.value = false
          lastAction.value = null
          suggestedActions.value = []

          for (const [seatIdx, seat] of message.state.seats.entries()) {
            const oldSeat = roomState.value.seats[seatIdx]!
            if (!seat || !oldSeat) {
              continue
            }

            const currentStack = seat?.stack + (message.state.hand?.bets[seat.playerId] || 0)
            if (
              seat &&
              oldSeat &&
              seat.playerId === oldSeat.playerId &&
              currentStack !== oldSeat.stack
            ) {
              const player = getPlayerById(seat.playerId)
              // if (currentStack > oldSeat.stack) {
              //   toast({
              //     message: 'Rebuy',
              //     details: `${player.name} stack was increased to ${currentStack}`
              //   })
              // } else {
              //   toast({
              //     message: 'Buy-out',
              //     details: `${player.name} stack was reduced to ${currentStack}`
              //   })
              // }
            }
          }
        }

        Object.assign(roomState.value, message.state)
        actionPending.value = false
        Object.keys(gameStateUpdateListeners.value).forEach((key) => {
          gameStateUpdateListeners.value[key]({
            state: roomState.value,
            playerId: message.playerId
          })
        })

        if (message.state.roomHistory.length) {
          roomHistoryList.value = message.state.roomHistory
        }
      } else {
        throw new Error('Unsupported update type')
      }
    },

    async init(token: string | null, passkey: string | null) {
      const params = new URLSearchParams(window.location.search)
      if (token && passkey) {
        this.joinRoom({
          token,
          name: null,
          roomId: roomId.value,
          nickname: params.get('nickname'),
          avatar: params.get('avatar')
        })
        return
      }
    },

    clean() {
      bridge?.close()
      bridge = null
      removeGameToken()
      loadState.value = 'loading'
      roomState.value = EMPTY_ROOM_STATE
      errorMessage.value = ''
      playerId.value = null
      autoAction.value = null
      winners.value = []
      actionPending.value = false
      dealerIndex.value = 0
      suggestedActions.value = []
      lastAction.value = null
      isReviewMode.value = false
      showButtons.value = false
      isHintMode.value = false
      playerCardsVisible.value = false
      correctMovesCount.value = 0
      roomId.value = null
      gameMode.value = null
      delta.value = 0
    },
    getSeatedTableId,
    setSeatedTableId,
    clearSeatedTableId,

    legalRaiseRange: computed(() => roomState.value.hand?.chipRange ?? { min: 0, max: 0 }),
    legalActions: computed(() => roomState.value.hand?.legalActions ?? []),
    raiseLevels: computed(() => roomState.value.hand?.raises ?? []),

    onGameStateUpdate(
      listener: (update: { state: RoomState; playerId: string }) => void,
      key?: string
    ) {
      if (!key)
        key =
          Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

      gameStateUpdateListeners.value[key] = listener
      return { key }
    },

    offGameStateUpdate(key: string) {
      delete gameStateUpdateListeners.value[key]
    }
  }
})

function mergeDeltaToRoomState(target: any, delta: any) {
  if (!delta) return
  for (const key of Object.keys(delta)) {
    if (key !== 'roomHistory') {
      target[key] = delta[key]
    }
  }
}
